import { useCallback, useSyncExternalStore } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * Toasts.
 *
 * Two jobs in this app, and only the second is cosmetic:
 *
 * 1. Failure on a write path MUST be visible (CONTRACT §7). A dose that looks
 *    logged but was not is the worst failure this app has. `toast.error` is how
 *    an optimistic update announces that it rolled back.
 * 2. Undo. Undoing a logged dose does not get a confirm dialog (UI rule 3) —
 *    it gets an undo toast, which is why `action` exists on the options.
 *
 * Implemented as a module-level store plus a Radix viewport, so `toast(...)`
 * can be called from a mutation callback with no hook and no context.
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  description?: string;
  /** Milliseconds. Errors default to longer because they must be read. */
  duration?: number;
  /** A single action button, e.g. Undo. */
  action?: ToastAction;
}

export interface ToastRecord extends ToastOptions {
  id: string;
  tone: ToastTone;
  title: string;
  /** Bumped to force Radix to re-open a toast reusing the same id. */
  createdAt: number;
}

const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 5000,
  // Long enough to read a sentence about a dose that did not save.
  error: 9000,
};

/** More than this on screen and none of them get read. */
const MAX_VISIBLE = 4;

let toasts: ToastRecord[] = [];
const listeners = new Set<() => void>();
let counter = 0;

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ToastRecord[] {
  return toasts;
}

function push(tone: ToastTone, title: string, options: ToastOptions = {}): string {
  counter += 1;
  const id = `toast-${counter}`;
  const record: ToastRecord = {
    id,
    tone,
    title,
    createdAt: Date.now(),
    duration: options.duration ?? DEFAULT_DURATION[tone],
    description: options.description,
    action: options.action,
  };
  toasts = [...toasts, record].slice(-MAX_VISIBLE);
  emit();
  return id;
}

function dismiss(id: string): void {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function dismissAll(): void {
  toasts = [];
  emit();
}

export interface ToastApi {
  (title: string, options?: ToastOptions): string;
  success: (title: string, options?: ToastOptions) => string;
  error: (title: string, options?: ToastOptions) => string;
  info: (title: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const toastFn = ((title: string, options?: ToastOptions) =>
  push('info', title, options)) as ToastApi;

toastFn.success = (title, options) => push('success', title, options);
toastFn.error = (title, options) => push('error', title, options);
toastFn.info = (title, options) => push('info', title, options);
toastFn.dismiss = dismiss;
toastFn.dismissAll = dismissAll;

export const toast: ToastApi = toastFn;

/* ------------------------------------------------------------------ *
 * Rendering
 * ------------------------------------------------------------------ */

const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

const TONE_CLASS: Record<ToastTone, string> = {
  success: 'border-status-taken/40 text-status-taken',
  error: 'border-status-missed/40 text-status-missed',
  info: 'border-line text-accent',
};

function ToastItem({ record }: { record: ToastRecord }): JSX.Element {
  const Icon = TONE_ICON[record.tone];
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) dismiss(record.id);
    },
    [record.id],
  );

  return (
    <ToastPrimitive.Root
      open
      onOpenChange={onOpenChange}
      duration={record.duration}
      // Errors are assertive: a failed dose log must interrupt, not queue
      // politely behind whatever the screen reader is currently saying.
      type={record.tone === 'error' ? 'foreground' : 'background'}
      className={cn(
        'pointer-events-auto flex w-full items-start gap-3 rounded-card border bg-surface p-3 shadow-pop',
        'data-[state=open]:animate-[medtrack-toast-in_150ms_ease-out]',
        'data-[state=closed]:animate-[medtrack-toast-out_150ms_ease-out]',
        'data-[swipe=end]:animate-[medtrack-toast-out_150ms_ease-out]',
        TONE_CLASS[record.tone],
      )}
    >
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />

      <div className="min-w-0 flex-1 space-y-0.5">
        <ToastPrimitive.Title className="text-sm font-medium leading-5 text-ink">
          {record.title}
        </ToastPrimitive.Title>
        {record.description ? (
          <ToastPrimitive.Description className="text-sm leading-5 text-muted">
            {record.description}
          </ToastPrimitive.Description>
        ) : null}
      </div>

      {record.action ? (
        <ToastPrimitive.Action
          asChild
          altText={record.action.label}
          onClick={record.action.onClick}
        >
          <button
            type="button"
            className={cn(
              'shrink-0 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-ink',
              'transition-colors duration-150 ease-out hover:bg-raised',
              'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            )}
          >
            {record.action.label}
          </button>
        </ToastPrimitive.Action>
      ) : null}

      <ToastPrimitive.Close
        aria-label="Dismiss"
        className={cn(
          '-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center rounded-md text-muted',
          'transition-colors duration-150 ease-out hover:bg-raised hover:text-ink',
          'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        )}
      >
        <X className="size-4" aria-hidden="true" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

export interface ToasterProps {
  /**
   * Keyboard shortcut that moves focus into the toast region.
   * Radix defaults to F8. Goes on the Viewport, not the Provider.
   */
  hotkey?: string[];
}

/**
 * Mount once, near the root. The viewport sits above the bottom tab bar on
 * mobile and clears the home indicator, so an error about a dose is never
 * hidden behind navigation.
 */
export function Toaster({ hotkey }: ToasterProps = {}): JSX.Element {
  const records = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {records.map((record) => (
        <ToastItem key={record.id} record={record} />
      ))}
      <ToastPrimitive.Viewport
        // `hotkey` is a Viewport prop in Radix, not a Provider prop. The
        // Provider handles duration and swipe; the Viewport owns the focus
        // region and therefore the shortcut that jumps into it.
        {...(hotkey ? { hotkey } : {})}
        className={cn(
          'pointer-events-none fixed z-[100] flex w-full flex-col gap-2 outline-none',
          // Above the bottom tab bar (h-16) plus the home indicator.
          'inset-x-0 bottom-0 p-3 pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))]',
          'sm:bottom-0 sm:right-0 sm:top-auto sm:w-full sm:max-w-sm sm:p-4',
          'lg:pb-[max(1rem,env(safe-area-inset-bottom,0px))]',
        )}
      />
    </ToastPrimitive.Provider>
  );
}
